/**
 * Generador de PDF para la tabla de medidas de mascotas
 * Diseño minimalista y animalista
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logoDotoni from '../assets/img/Logo.jpg';

const loadImageAsBase64 = (url) =>
  fetch(url)
    .then((r) => r.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onloadend = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(blob);
        })
    );

const MEASURE_INSTRUCTIONS = {
  chest_girth: 'Contorno del pecho: Medir alrededor de la parte más ancha del pecho, justo detrás de las patas delanteras. La cinta debe quedar cómoda pero ajustada.',
  back_length: 'Largo de espalda: Desde la base del cuello (donde empieza la espalda) hasta la base de la cola. Sigue la curvatura natural del lomo.',
  neck_girth: 'Contorno del cuello: Alrededor del cuello, en el punto donde iría el collar. Deja espacio para un dedo entre la cinta y el cuello.',
  approx_weight: 'Peso aproximado: Pesar a la mascota en báscula. Si no tienes báscula, consulta con el veterinario o usa el peso de la última visita.',
  breed_type: 'Tipo de raza: Clasificación por tamaño (Pequeño, Mediano, Grande) según la complexión de la mascota.',
};

const COLORS = {
  primary: [51, 46, 90],
  accent: [234, 157, 14],
  light: [248, 250, 252],
  text: [55, 65, 81],
  textMuted: [107, 114, 128],
};

/**
 * Genera el PDF de la tabla de medidas
 * @param {Array} sizeSpecs - Array de medidas (size specs)
 * @param {Function} getMeasureValue - Función para obtener valor de medida
 * @param {Array} measureColumns - Columnas de medidas [{ key, label }]
 * @param {Object} branding - { companyName, logoUrl } opcional desde BrandingContext
 */
export const generateMeasuresPdf = async (sizeSpecs, getMeasureValue, measureColumns, branding = {}) => {
  const companyName = branding.companyName || 'DotoniWoof';
  const logoUrl = branding.logoUrl;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 0;

  doc.setFont('helvetica');

  // Header - Banner azul con logo alineado a la izquierda
  const headerHeight = 35;
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  // Logo dentro del banner, alineado a la izquierda, manteniendo proporciones
  const logoWidth = 24;
  const logoHeight = 22;
  const logoX = 10;
  const logoY = (headerHeight - logoHeight) / 2;

  const logoSource = logoUrl || logoDotoni;
  try {
    const logoBase64 = await loadImageAsBase64(logoSource);
    doc.addImage(logoBase64, 'JPEG', logoX, logoY, logoWidth, logoHeight);
  } catch (_) {
    try {
      const logoBase64 = await loadImageAsBase64(logoDotoni);
      doc.addImage(logoBase64, 'JPEG', logoX, logoY, logoWidth, logoHeight);
    } catch (_) {
      // Si falla la carga del logo, continuar sin él
    }
  }

  // Texto a la derecha del logo
  const textX = logoX + logoWidth + 10;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Tabla de Medidas', textX, 14);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`Guía para prendas de mascotas • ${companyName}`, textX, 22);
  doc.text(new Date().toLocaleDateString('es-CO', { dateStyle: 'long' }), textX, 28);
  doc.setTextColor(...COLORS.text);
  y = headerHeight + 10;

  // Sección: Cómo tomar las medidas
  const instrItems = measureColumns.filter((c) => MEASURE_INSTRUCTIONS[c.key]);
  let instrContentHeight = 14;
  for (const c of instrItems) {
    const text = `• ${c.label}: ${MEASURE_INSTRUCTIONS[c.key]}`;
    const lines = doc.splitTextToSize(text, pageWidth - 48);
    instrContentHeight += lines.length * 4 + 3;
  }
  const instrBoxHeight = Math.min(instrContentHeight, 75);

  doc.setFillColor(...COLORS.light);
  doc.rect(14, y, pageWidth - 28, instrBoxHeight, 'F');
  doc.setDrawColor(...COLORS.primary);
  doc.setLineWidth(0.5);
  doc.rect(14, y, pageWidth - 28, instrBoxHeight, 'S');
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Cómo tomar las medidas a tu mascota', 20, y + 8);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.textMuted);

  let instrY = y + 16;
  for (const c of instrItems) {
    const text = `• ${c.label}: ${MEASURE_INSTRUCTIONS[c.key]}`;
    const lines = doc.splitTextToSize(text, pageWidth - 48);
    doc.text(lines, 20, instrY);
    instrY += lines.length * 4 + 3;
  }
  y += instrBoxHeight + 8;

  // Tabla de medidas
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.primary);
  doc.text('Tabla de tallas', 14, y + 6);
  y += 12;

  const headers = ['Talla', 'Variante', ...measureColumns.map((c) => c.label)];
  const rows = (sizeSpecs || []).map((item) => [
    item.size_code || '-',
    item.size_variant || item.variant || '-',
    ...measureColumns.map((c) => {
      const v = getMeasureValue ? getMeasureValue(item, c.key) : item[c.key];
      return v != null && v !== '' ? String(v) : '-';
    }),
  ]);

  autoTable(doc, {
    startY: y,
    head: [headers],
    body: rows,
    theme: 'striped',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9,
    },
    bodyStyles: {
      fontSize: 8,
      textColor: COLORS.text,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    margin: { left: 14, right: 14 },
    tableWidth: 'auto',
  });

  y = doc.lastAutoTable.finalY + 15;

  // Nota al pie
  if (y < 270) {
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.textMuted);
    doc.text(
      'Medidas en centímetros (cm). Para mayor precisión, toma las medidas con la mascota de pie y relajada.',
      14,
      y
    );
  }

  doc.save(`tabla-medidas-${new Date().toISOString().slice(0, 10)}.pdf`);
};
